import isNode from 'detect-node';
import {
  ApiError,
  MykissApolloError,
  MykissApolloNotFoundError,
  MykissGraphQLError,
} from './errorClasses';
import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors';
import { GraphQLError } from 'graphql';
import { logger } from '@lib/logger/logger';

import { captureError } from './sentry';

const getContext = (): string | undefined => {
  return isNode ? undefined : window.location.pathname;
};

export const isApolloError = (error: unknown): error is ErrorLike => {
  return CombinedGraphQLErrors.is(error) || ServerError.is(error) || ServerParseError.is(error);
};

export const isApolloNotFoundError = (error: ErrorLike): boolean => {
  return (
    error.message.startsWith(`GraphQL error: Couldn't find`) ||
    error.message.startsWith("Couldn't find `User` with given arguments") ||
    error.message.startsWith("Couldn't find `FishingWater` with given arguments") ||
    error.message.startsWith("Couldn't find `Place` with given arguments") ||
    error.message.startsWith("Couldn't find `Op::Post` with given arguments") ||
    error.message.startsWith("couldn't understand how to find `Op::Post` with id") ||
    error.message.startsWith("Couldn't find `Species` with given arguments") ||
    error.message.startsWith("couldn't understand how to find `Species` with id") ||
    error.message.startsWith("Couldn't find `Region` with `id`") ||
    error.message.startsWith("Couldn't find `Equipment::Product` with given arguments") ||
    error.message.startsWith("Couldn't find `Catch` with `ID`")
  );
};

const convertApolloError = (error: ErrorLike): MykissApolloError | MykissApolloNotFoundError => {
  if (isApolloNotFoundError(error)) {
    return new MykissApolloNotFoundError(error.message, error);
  }
  return new MykissApolloError(error);
};

export const reportAPIError = (
  error: Response,
  severity?: 'info' | 'warning',
  otherData?: object,
  context: string | undefined = getContext(),
): void => {
  error
    .text()
    .then((errorText: string) => {
      captureError(new ApiError(error, errorText), {
        context,
        severity: severity ?? 'error',
        metadata: {
          responseStatus: error.status,
          url: error.url,
          responseErrorText: errorText,
          ...(otherData as Record<string, unknown>),
        },
        fingerprint: [context ?? 'unknown', String(error.status), errorText],
      });
    })
    .catch(captureError);
};

const IGNORED_APOLLO_ERRORS = [
  "Couldn't find `FishingWater` with given arguments",
  "Couldn't find `Place` with given arguments",
  "Couldn't find `User` with given arguments",
];

// We receive a lot of random traffic to pages with removed or non-existent arguments. While its
// nice to know that this occurs, it chokes up our logs and error reports. We're silencing these
// particular errors - if you need a better idea of 404 rates, look at Cloudfront logs in Datadog.
function shouldIgnoreApolloError(error: MykissApolloError | MykissApolloNotFoundError): boolean {
  return IGNORED_APOLLO_ERRORS.some(ignoredError => error.message.startsWith(ignoredError));
}

export const reportApolloError = (
  error: ErrorLike,
  severity?: 'info' | 'warning',
  otherData?: object,
  context: string | undefined = getContext(),
): void => {
  const convertedError = convertApolloError(error);

  if (shouldIgnoreApolloError(convertedError)) {
    logger.debug(convertedError);
    return;
  }

  captureError(convertedError, {
    context,
    severity:
      convertedError.name === 'MykissApolloNotFoundError'
        ? (severity ?? 'info')
        : (severity ?? 'error'),
    metadata: {
      graphQLErrors: JSON.stringify(convertedError.graphQLErrors),
      networkError: convertedError.networkError,
      ...(otherData as Record<string, unknown>),
    },
  });
};

export const reportGraphQLError = (
  error: GraphQLError,
  severity?: 'info' | 'warning',
  otherData?: object,
  context: string | undefined = getContext(),
): void => {
  const convertedError = new MykissGraphQLError(error);
  captureError(convertedError, {
    context,
    severity: severity ?? 'error',
    metadata: {
      locations: convertedError.locations,
      path: convertedError.path,
      ...(otherData as Record<string, unknown>),
    },
  });
};
